import { queryOptions } from '@tanstack/react-query'
import { apiGet } from './client'
import { rangeQuery, type Range } from '../easeedesk/dashboards/range'

/**
 * Tablourile Easeedesk mutate din `application/controllers/easeedesk-dashboards/`.
 *
 * Stau in fisierul lor, nu in `api/easeedeskBoards.ts`: acolo scriu in acelasi
 * timp alte benzi, si doua ecrane in acelasi fisier inseamna o impacare in plus
 * la fiecare livrare.
 *
 * `apiGet` pune EL `/api/v2` in fata caii - nu se mai scrie o data aici.
 */

export interface DashboardColumn {
  key: string
  label: string
  width: number
}

export interface QuickSupportRow {
  id: number
  name: string
  new: number
  live: number
  remote_control: number
  delayeds: number
  overdue: number
  lost: number
  resolved: number
  closed: number
  closing: number
}

export interface QuickSupportDashboard {
  ready: boolean
  from: string
  to: string
  title: string
  columns: DashboardColumn[]
  rows: QuickSupportRow[]
}

export interface GroupUsersTicketType {
  ticket_type: number
  color_identifier: string
  short_name: string
  active: number
  resolved: number
}

export interface GroupUsersRow {
  assignee: number
  username: string
  group_id: number
  group_color: string
  online: boolean
  escalated: number
  connected: number
  total_active: number
  total_resolved: number
  total_score: number
  sla_score: number
  rework: number
  tickets: GroupUsersTicketType[]
}

export interface GroupUsersDashboard {
  ready: boolean
  groups: { group_id: number; group_name: string; group_color: string }[]
  user_tickets: GroupUsersRow[]
}

export interface GroupUsersPage {
  ready: boolean
  title: string
  groups: { id: number; name: string }[]
}

export const groupUsersPageQuery = queryOptions({
  queryKey: ['easeedesk', 'dashboards', 'group-users'],
  queryFn: () => apiGet<GroupUsersPage>('/easeedesk/dashboards/group-users'),
})

export function groupUsersRowsQuery(groups: number[], range: Range) {
  const p = new URLSearchParams({ from: range.from, to: range.to })
  for (const id of groups) p.append('groups[]', String(id))
  const query = p.toString()

  return queryOptions({
    queryKey: ['easeedesk', 'dashboards', 'group-users', 'rows', query],
    queryFn: () => apiGet<GroupUsersDashboard>(`/easeedesk/dashboards/group-users/rows?${query}`),
  })
}

export interface UsersTicketsOption {
  id: number
  name: string
}

export interface UsersTicketsType extends UsersTicketsOption {
  short_name: string
  color: string
  /** 1 = tipul e facut de masina; macheta veche il lasa nebifat. */
  automatic: number
}

export interface UsersTicketsPage {
  ready: boolean
  title: string
  groups: UsersTicketsOption[]
  types: UsersTicketsType[]
  statuses: (UsersTicketsOption & { color: string })[]
}

export interface UsersTicketsRow {
  user_id: number
  username: string
  group_id: number
  group_name: string
  group_color: string
  online: boolean
  last_activity: number | null
  active: number
  escalated: number
  oldest_active: number
  resolved: number
  avg_resolve: number
  over_sla: number
}

export interface UsersTicketsBar {
  name: string
  color: string
  total: number
}

export interface UsersTicketsTicket {
  id: number
  subject: string
  created: string
  resolved: string | null
  escalation_type: number
  type_name: string
  short_name: string
  color: string
  status_name: string
  status_color: string
  company_name: string
  age: number
}

export interface UsersTicketsOldest {
  id: number
  subject: string
  created: string
  age: number
  short_name: string
  color: string
  company_name: string
  assignee_name: string
}

export interface UsersTicketsSummary {
  ready: boolean
  users: UsersTicketsRow[]
  by_type: UsersTicketsBar[]
  by_status: UsersTicketsBar[]
  unassigned: number
  oldest_open: UsersTicketsOldest[]
  previous_resolved: number
}

export type UsersTicketsScope = 'active' | 'resolved' | 'all'

export interface UsersTicketsFilters {
  scope: UsersTicketsScope
  from: string
  to: string
  groups: number[]
  types: number[]
  statuses: number[]
}

/** `?scope=&from=&to=&groups[]=...`, exact ce citea capatul vechi. */
export function usersTicketsQueryString(filters: UsersTicketsFilters): string {
  const p = new URLSearchParams()
  p.set('scope', filters.scope)
  if (filters.from) p.set('from', filters.from)
  if (filters.to) p.set('to', filters.to)
  for (const [name, values] of [
    ['groups', filters.groups],
    ['types', filters.types],
    ['statuses', filters.statuses],
  ] as const) {
    for (const value of values) p.append(`${name}[]`, String(value))
  }

  return p.toString()
}

export const usersTicketsPageQuery = queryOptions({
  queryKey: ['easeedesk', 'dashboards', 'users-tickets'],
  queryFn: () => apiGet<UsersTicketsPage>('/easeedesk/dashboards/users-tickets'),
})

export function usersTicketsSummaryQuery(filters: UsersTicketsFilters) {
  const query = usersTicketsQueryString(filters)

  return queryOptions({
    queryKey: ['easeedesk', 'dashboards', 'users-tickets', 'summary', query],
    queryFn: () => apiGet<UsersTicketsSummary>(`/easeedesk/dashboards/users-tickets/summary?${query}`),
  })
}

export function usersTicketsUserQuery(userId: number, filters: UsersTicketsFilters) {
  const query = usersTicketsQueryString(filters)

  return queryOptions({
    queryKey: ['easeedesk', 'dashboards', 'users-tickets', 'user', userId, query],
    queryFn: () => apiGet<{ tickets: UsersTicketsTicket[] }>(`/easeedesk/dashboards/users-tickets/user/${userId}?${query}`),
  })
}

export function usersTicketsUnassignedQuery(filters: UsersTicketsFilters) {
  const query = usersTicketsQueryString(filters)

  return queryOptions({
    queryKey: ['easeedesk', 'dashboards', 'users-tickets', 'unassigned', query],
    queryFn: () => apiGet<{ tickets: UsersTicketsTicket[] }>(`/easeedesk/dashboards/users-tickets/unassigned?${query}`),
  })
}

/** O casuta simpla, cu o cifra, si scara pe care isi ia culoarea. */
export interface SkuCountTile {
  kind: 'count'
  key: string
  label: string
  value: number
  scale: 'queue' | 'unassigned' | 'lost' | 'plain'
}

/** O casuta de doua randuri: media sus, cate au trecut de prag jos. */
export interface SkuPairTile {
  kind: 'pair'
  key: string
  label: string
  top: string
  bottom_label: string
  bottom: string
}

export type SkuTile = SkuCountTile | SkuPairTile

export interface SkuDashboard {
  ready: boolean
  from: string
  to: string
  title: string
  tiles: SkuTile[]
}

export function skuDashboardQuery(range: Range) {
  return queryOptions({
    queryKey: ['easeedesk', 'dashboards', 'sku', range.from, range.to],
    queryFn: () => apiGet<SkuDashboard>(`/easeedesk/dashboards/sku?${rangeQuery(range)}`),
    /* Ecranul de perete se reimprospata singur; aici la fel, la un minut. */
    refetchInterval: 60_000,
  })
}

export interface OdpStageCount {
  all: number
  home: number
  store: number
}

export interface OdpSku {
  sku_id: number
  group_name: string
  manual_commands: number
  sended: OdpStageCount
  activated: OdpStageCount
  finished: OdpStageCount
  cancelled: OdpStageCount
}

export interface OdpWorkorderDashboard {
  ready: boolean
  from: string
  to: string
  title: string
  resolved: { operator: number; support_request: number; chat: number; phone_support: number }
  all_sended: number
  all_cancelled: number
  skus: OdpSku[]
}

export function odpWorkorderQuery(range: Range) {
  return queryOptions({
    queryKey: ['easeedesk', 'dashboards', 'odp-workorder', range.from, range.to],
    queryFn: () => apiGet<OdpWorkorderDashboard>(`/easeedesk/dashboards/odp-workorder?${rangeQuery(range)}`),
  })
}

export type RegionSkuKind = 'all' | 'new' | 'existing' | 'all_sku'

export interface RegionSkuStore {
  id: number
  total: number
}

export interface RegionSkuDistrict {
  id: number
  total: number
  stores: RegionSkuStore[]
}

export interface RegionSkuRegion {
  region_id: number
  region_name: string
  total: number
  districts: RegionSkuDistrict[]
}

export interface RegionSkuDashboard {
  ready: boolean
  from: string
  to: string
  type: RegionSkuKind
  title: string
  total: number
  regions: RegionSkuRegion[]
}

export function regionSkuQuery(kind: RegionSkuKind, range: Range) {
  return queryOptions({
    queryKey: ['easeedesk', 'dashboards', 'region-sku', kind, range.from, range.to],
    queryFn: () =>
      apiGet<RegionSkuDashboard>(`/easeedesk/dashboards/region-sku?type=${kind}&${rangeQuery(range)}`),
  })
}

export interface RemarketingWeek {
  week: string
  start: string
  end: string
  year: string
  from: string
  to: string
}

export interface RemarketingRow {
  title: string
  value: number
}

export interface RemarketingDashboard {
  ready: boolean
  from: string
  to: string
  title: string
  weeks: RemarketingWeek[]
  current: RemarketingRow[]
  total: RemarketingRow[]
}

/**
 * Ecranul de remarketing nu are scurtaturi de perioada, ci o lista de
 * saptamani; de-aia primeste doua zile, nu un `Range`.
 */
export function remarketingQuery(from: string, to: string) {
  return queryOptions({
    queryKey: ['easeedesk', 'dashboards', 'remarketing', from, to],
    queryFn: () =>
      apiGet<RemarketingDashboard>(
        `/easeedesk/dashboards/remarketing?${new URLSearchParams(from && to ? { from, to } : {}).toString()}`
      ),
  })
}

export interface LeaderBoardRow {
  user_id: number
  name: string
  avg_tookover: number
  avg_progress: number
  avg_connected: number
  avg_resolved: number
  avg_feedback: number
  hit_tookover: number
  hit_sla: number
  hit_connected: number
  miss_tookover: number
  miss_sla: number
  miss_connected: number
  final_score: number
  tickets: number
}

export interface LeaderBoardDashboard {
  ready: boolean
  from: string
  to: string
  title: string
  /** 0 = furnizorul asta n-are clasament, deci tabelul e gol. */
  group: number
  columns: DashboardColumn[]
  rows: LeaderBoardRow[]
  /** null = niciun rand, deci "Total TP" nu se deseneaza, ca in ecranul vechi. */
  total: number | null
}

export function leaderBoardQuery(range: Range) {
  return queryOptions({
    queryKey: ['easeedesk', 'dashboards', 'leader-board', range.from, range.to],
    queryFn: () => apiGet<LeaderBoardDashboard>(`/easeedesk/dashboards/leader-board?${rangeQuery(range)}`),
  })
}

export function quickSupportQuery(range: Range) {
  return queryOptions({
    queryKey: ['easeedesk', 'dashboards', 'quick-support', range.from, range.to],
    queryFn: () => apiGet<QuickSupportDashboard>(`/easeedesk/dashboards/quick-support?${rangeQuery(range)}`),
    /* Ecranele de perete se reimprospatau singure la un minut. */
    refetchInterval: 60_000,
  })
}
